import {
  READER_ID_PARAM,
  RETURN_TO_PARAM,
} from "@bindery/shared/contracts/session";
import { peersFor, targetOfHost } from "@bindery/topology";
import {
  READER_SESSION_PATH,
  VIA_DEVICE,
  VIA_PARAM,
} from "@/contracts/session";
import { lastReader } from "@/storage/last-reader";

export const SIGN_IN_PAGE = "/sign-in";
export const BROKER_PARENT_PATH = "/parent";

// Sign-in and management are not links: they go through ./actions, which
// prove the reader came from this app.

function withReturnTo(path: string, returnTo: string): string {
  return `${path}?${new URLSearchParams({ [RETURN_TO_PARAM]: returnTo }).toString()}`;
}

/** "Save progress": the /sign-in page, which reads the reader from the session. */
export function saveProgressHref(returnTo: string): string {
  return withReturnTo(SIGN_IN_PAGE, returnTo);
}

// The same topology row the Worker's auth consumer defaults to; a host the
// topology does not name (localhost) gets the dev peer.
export function brokerOrigin(hostname: string): string {
  return peersFor(targetOfHost(hostname) ?? "dev").authOrigin;
}

/** Needs no sign-in state, so a plain link to the broker's /parent. */
export function parentHref(hostname: string = location.hostname): string {
  return new URL(BROKER_PARENT_PATH, brokerOrigin(hostname)).toString();
}

/**
 * The device's identify-only entry: back to the device's own reader, else
 * none and the Worker mints one. The app names a reader only when it is
 * the device's, so the Worker lands it ungated, unlike a scanned bookmark.
 */
export function readerSessionPath(
  deviceReaderId: string | null,
  returnTo?: string,
): string {
  const params = new URLSearchParams();
  if (deviceReaderId) {
    params.set(READER_ID_PARAM, deviceReaderId);
    params.set(VIA_PARAM, VIA_DEVICE);
  }
  if (returnTo) params.set(RETURN_TO_PARAM, returnTo);
  const query = params.toString();
  return query === "" ? READER_SESSION_PATH : `${READER_SESSION_PATH}?${query}`;
}

/** Resumes the reader this device last read as; only a device with none mints one. */
export function startReadingHref(
  deviceReaderId: string | null = lastReader(),
): string {
  return readerSessionPath(deviceReaderId, "/reader");
}
